import { DEFAULT_HIGHLIGHT_COLOR } from "@/features/rich-text/extensions/highlight-colors";

/**
 * The slice of hast this transform needs. Typed structurally rather than through `hast`:
 * `@types/hast` is a transitive dependency of react-markdown, not one this app declares.
 */
interface HastNodeLike {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown> | null;
  children?: HastNodeLike[];
}

/**
 * `==text==`, the `markdown-it-mark` syntax the backend Markdown runtime already speaks and
 * the one Claudia writes for placeholder content — in chat messages as well as in drafts.
 *
 * This is the gate on the raw Markdown string, not the pairing rule: the transform below
 * pairs delimiters across the hast tree, where the content between them may be several
 * nodes. Both ends must hug a non-whitespace, non-`=` character, which is exactly what the
 * emphasis, code and link markup of a rich highlight (`==**launch date**==`) puts there, and
 * which keeps `====` and ordinary prose such as `a == b == c` out. Content may span a line
 * break, matching `markdown-it-mark` (its delimiters are flanking-scanned, and `\n` inside a
 * paragraph is plain whitespace to that scan).
 *
 * The gate is allowed to be looser than the transform — a false positive only means the text
 * takes the Markdown path — but must never be tighter, or a highlight would never be parsed.
 *
 * Deliberately not global: a `lastIndex` shared between {@link hasMessageHighlight} and the
 * splitter would make either one's result depend on the other's last call.
 */
const HIGHLIGHT_PATTERN = /==[^\s=](?:[\s\S]*?[^\s=])?==/;

/** hast spells `data-highlight-color` camelCased; react-markdown renders it back dashed. */
const HIGHLIGHT_COLOR_PROPERTY = "dataHighlightColor";

/** Elements whose text is verbatim, so `==` inside them is content rather than markup. */
const VERBATIM_TAG_NAMES = new Set(["code", "pre"]);

/** The literal a delimiter falls back to when it never finds its partner. */
const DELIMITER = "==";

/** Whether `value` carries at least one highlight this transform would render. */
export function hasMessageHighlight(value: string): boolean {
  return HIGHLIGHT_PATTERN.test(value);
}

/**
 * A parent's inline children, flattened so delimiters are addressable across node boundaries:
 * text runs and `==` delimiters come from direct text children, every other child stays whole.
 */
type InlineUnit =
  | { kind: "text"; value: string }
  | { kind: "delimiter" }
  | { kind: "node"; node: HastNodeLike };

function pushTextUnits(units: InlineUnit[], value: string): void {
  let pending = "";
  let index = 0;

  // A run of `n` `=` carries `floor(n / 2)` delimiters; an odd leftover stays literal text.
  for (const match of value.matchAll(/=+/g)) {
    const run = match[0];
    pending += value.slice(index, match.index);
    index = match.index + run.length;

    if (run.length < 2) {
      pending += run;
      continue;
    }
    // `markdown-it` splits an odd run as `=` + `==`…, leaving the stray marker in the text.
    if (run.length % 2 === 1) pending += "=";
    if (pending) {
      units.push({ kind: "text", value: pending });
      pending = "";
    }
    for (let count = Math.floor(run.length / 2); count > 0; count -= 1) {
      units.push({ kind: "delimiter" });
    }
  }

  pending += value.slice(index);
  if (pending) units.push({ kind: "text", value: pending });
}

function toInlineUnits(children: HastNodeLike[]): InlineUnit[] {
  const units: InlineUnit[] = [];
  for (const child of children) {
    if (child.type === "text") {
      pushTextUnits(units, child.value ?? "");
      continue;
    }
    units.push({ kind: "node", node: child });
  }
  return units;
}

/**
 * An opener needs content right after it, a closer content right before it — an element
 * counts, a whitespace-edged text run does not, and a neighbouring delimiter is not content
 * at all, which is what keeps `====` and `== TBD ==` literal.
 */
function canOpen(units: InlineUnit[], index: number): boolean {
  const next = units[index + 1];
  if (!next) return false;
  if (next.kind === "node") return true;
  if (next.kind === "delimiter") return false;
  return !/^\s/.test(next.value);
}

function canClose(units: InlineUnit[], index: number): boolean {
  const previous = units[index - 1];
  if (!previous) return false;
  if (previous.kind === "node") return true;
  if (previous.kind === "delimiter") return false;
  return !/\s$/.test(previous.value);
}

/** The nearest closer after `openIndex`, or `-1` — non-greedy, so `==a==b==` marks only `a`. */
function findCloser(units: InlineUnit[], openIndex: number): number {
  for (let index = openIndex + 1; index < units.length; index += 1) {
    const unit = units[index];
    if (unit?.kind === "delimiter" && canClose(units, index)) return index;
  }
  return -1;
}

/** Emits `units[start, end)` as hast, unpaired delimiters falling back to literal `==`. */
function emitUnits(units: InlineUnit[], start: number, end: number): HastNodeLike[] {
  const nodes: HastNodeLike[] = [];
  let literal = "";

  const flush = () => {
    if (!literal) return;
    nodes.push({ type: "text", value: literal });
    literal = "";
  };

  for (let index = start; index < end; index += 1) {
    const unit = units[index];
    if (!unit) continue;
    if (unit.kind === "text") {
      literal += unit.value;
      continue;
    }
    if (unit.kind === "delimiter") {
      literal += DELIMITER;
      continue;
    }
    flush();
    nodes.push(unit.node);
  }

  flush();
  return nodes;
}

function createHighlightNode(children: HastNodeLike[]): HastNodeLike {
  return {
    type: "element",
    tagName: "mark",
    // Markdown carries no color, so chat renders the editor's default. Stamping it rather
    // than leaving a bare `mark` keeps one styling contract across both surfaces.
    properties: { [HIGHLIGHT_COLOR_PROPERTY]: DEFAULT_HIGHLIGHT_COLOR },
    children,
  };
}

/** The children `units` become once paired delimiters are marks, or `null` when none pair. */
function pairHighlights(units: InlineUnit[]): HastNodeLike[] | null {
  const nodes: HastNodeLike[] = [];
  let pending = 0;
  let index = 0;

  while (index < units.length) {
    const unit = units[index];
    if (unit?.kind !== "delimiter" || !canOpen(units, index)) {
      index += 1;
      continue;
    }
    const closeIndex = findCloser(units, index);
    if (closeIndex === -1) {
      index += 1;
      continue;
    }

    nodes.push(...emitUnits(units, pending, index));
    nodes.push(createHighlightNode(emitUnits(units, index + 1, closeIndex)));
    // Scanning resumes past the mark: its content is settled, and its element children were
    // already visited on the way down.
    index = closeIndex + 1;
    pending = index;
  }

  if (pending === 0) return null;
  nodes.push(...emitUnits(units, pending, units.length));
  return nodes;
}

function transformHighlightsInNode(node: HastNodeLike): void {
  const children = node.children;
  if (!Array.isArray(children)) return;

  for (const child of children) {
    if (child.type !== "element") continue;
    if (VERBATIM_TAG_NAMES.has(child.tagName ?? "")) continue;
    transformHighlightsInNode(child);
  }

  const paired = pairHighlights(toInlineUnits(children));
  if (paired) node.children = paired;
}

/**
 * Renders `==text==` as the same `<mark data-highlight-color="yellow">` the entity editor's
 * highlight mark produces, so a placeholder Claudia writes in a chat message reads as a
 * highlight instead of as literal `==` signs.
 *
 * Delimiters are paired across a parent's inline children, so a highlight may wrap emphasis,
 * links or inline code — `==**launch date**==` is a bold highlight — and the reverse nesting
 * `**==TBD==**` still works because every element is visited before its parent pairs. Only
 * direct text children can carry a delimiter, so `==` inside `code` or `pre` is content and
 * can never open or close a highlight.
 */
export function rehypeMessageHighlights() {
  return (tree: unknown) => {
    if (!tree || typeof tree !== "object") return;
    transformHighlightsInNode(tree as HastNodeLike);
  };
}
